/**
 * Recovers from stale dynamic-import / chunk-load failures.
 *
 * After a new deploy, clients still holding the previous `index.html` request
 * hashed asset chunks (e.g. `/assets/index-DSGiT2Sy.js`) that no longer exist
 * on the server. Lazy route/component imports then reject with "Load failed" /
 * "Failed to fetch dynamically imported module". A transient network blip on a
 * lazy chunk produces the same symptom.
 *
 * The fix is a one-time navigation to a cache-busted URL, which re-fetches the
 * current `index.html` and the asset hashes it references. A sessionStorage
 * flag guards against an infinite loop when that navigation cannot recover.
 * A deliberate browser refresh remains a new attempt: the guard must not turn
 * one failed automatic retry into a broken tab until the user opens a new one.
 */

const RELOAD_GUARD_KEY = 'borradh:chunk-reload-attempted';
export const RELOAD_MARKER_PARAM = '__borradh_chunk_reload';

function warnChunkReload(message: string, extra?: Record<string, unknown>) {
  console.warn('[app.chunkReload]', message, extra ?? '');
}

/** Heuristics for a dynamic-import / chunk-load failure across browsers. */
function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  if (!message) return false;
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Load failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Unable to preload CSS/i.test(message)
  );
}

/**
 * Reload the page once to pick up fresh chunk hashes. Returns `true` when a
 * reload was triggered, `false` when the guard already fired this session.
 */
function reloadOnceForStaleChunk(reason: string): boolean {
  let alreadyAttempted = false;
  try {
    alreadyAttempted = sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
  } catch {
    // sessionStorage can throw (private mode / disabled storage); fall back to
    // not reloading rather than risk a loop we can't track.
    alreadyAttempted = true;
  }

  if (alreadyAttempted) {
    warnChunkReload('Stale chunk after reload — not retrying', { reason });
    return false;
  }

  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  } catch {
    // If we can't persist the guard we'd risk an infinite loop, so bail out.
    return false;
  }

  // A distinct URL bypasses a browser's stale navigation entry. `reload()` is
  // allowed to reuse it, which is why a repeatedly refreshed existing tab could
  // remain broken while a new tab worked.
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_MARKER_PARAM, '1');
  warnChunkReload('Navigating to recover from stale chunk', { reason });
  window.location.replace(url.toString());
  return true;
}

/**
 * Clear the reload guard once the app has booted successfully, so a future
 * stale-deploy in the same tab can recover again.
 */
export function clearChunkReloadGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // ignore
  }
}

/**
 * Preserve the guard across our own recovery navigation, but clear it when a
 * person refreshes the page later. The marker is removed from the visible URL
 * immediately; the guard itself remains until a successful boot so a second
 * chunk failure during the recovery cannot loop forever.
 */
function resetGuardForManualNavigation(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(RELOAD_MARKER_PARAM)) {
      clearChunkReloadGuard();
      return;
    }
    url.searchParams.delete(RELOAD_MARKER_PARAM);
    window.history.replaceState(window.history.state, '', url);
  } catch {
    // Keep the existing guard if URL/session storage is unavailable: avoiding a
    // reload loop is safer than an extra recovery attempt in this rare case.
  }
}

/**
 * Install global listeners that auto-recover from stale chunk loads. Call once
 * at app entry, before the router mounts.
 */
export function installChunkReloadHandler(): void {
  resetGuardForManualNavigation();
  // Vite emits this when a module preload (lazy route/component) fails.
  window.addEventListener('vite:preloadError', (event) => {
    const detail = (event as Event & { payload?: unknown }).payload;
    const isChunkError = detail === undefined || isChunkLoadError(detail);
    if (isChunkError && reloadOnceForStaleChunk('vite:preloadError')) {
      // Prevent Vite from throwing the unhandled preload error before we reload.
      event.preventDefault();
    }
    if (detail instanceof Error && !isChunkError) {
      // Non-chunk preload error — surface it for visibility.
      warnChunkReload('preloadError (non-chunk)', { message: detail.message });
    }
  });

  // Catch dynamic-import rejections that don't surface via vite:preloadError
  // (e.g. a lazy import awaited inside a loader without a preload step).
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      if (reloadOnceForStaleChunk('unhandledrejection')) {
        event.preventDefault();
      }
    }
  });
}
